import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import moment from 'moment-timezone';
import * as TzLookup from 'tz-lookup';

import { CtaVariantEnum } from '~/component/call-to-action/enums/variant.enum';
import { DateTimeInterface } from '~/component/form/datetime/interfaces/datetime.interface';
import { GeoPointInterface } from '@sport-activities/sdk';
import { Type } from '~/component/form/input/enums/type.enum';
import { ValueAccessorBase } from '~/component/value-accessor.base';
import { dateRangeValidator } from '~/core/validators/date/date-range.validator';
import { ToggleService } from '~/toggles/services/toggle.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';

import { weekConfig } from './date-selection.component.config';
import { ActivityPlanningDateSelectionInterface } from './interfaces/date-selection.interface';
import { ActivityPlanningDateSelectionWeekConfigInterface } from './interfaces/week.config.interface';
import { ActivityPlanningDateSelectionWeekInterface } from './interfaces/week.interface';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';

@Component({
  selector: 'app-organizer-activity-planning-date-selection',
  templateUrl: './date-selection.component.html',
  styleUrls: ['./date-selection.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: OrganizerActivityPlanningDateSelectionComponent,
      multi: true,
    },
  ],
})
export class OrganizerActivityPlanningDateSelectionComponent
  extends ValueAccessorBase<ActivityPlanningDateSelectionInterface>
  implements OnInit, OnDestroy
{
  @Input() public defaultValue?: {
    dateSelection: ActivityPlanningDateSelectionInterface;
  };
  @Input() public isLive: boolean;
  @Input() public location: GeoPointInterface;
  @Input() public showRecurrentSection = true;
  @Output() public readonly deleteCard = new EventEmitter();

  private readonly recurrentDurationMonths =
    this.toggleService.getVariantString(
      ToggleNameEnum.OrganizerMaxRecurrenceScheduleMonths,
      '3'
    );
  private readonly weekConfig: ActivityPlanningDateSelectionWeekConfigInterface[] =
    weekConfig;
  public readonly deleteIconType = VtmnPlayIconTypeEnum.Close;
  public readonly iconTypeEnum = VtmnPlayIconTypeEnum;
  public readonly inputTypeEnum = Type;
  public readonly link2Variant = CtaVariantEnum.Link2;
  public readonly startDateMin = new Date();
  public readonly toggleNameEnum = ToggleNameEnum;

  private formGroupSubscription: Subscription;
  private recurrentSwitchSubscription: Subscription;
  private startDateTimeSubscription: Subscription;
  public endDateMax = new Date();
  public endDateMin = new Date();
  public formGroup: FormGroup;
  public week: ActivityPlanningDateSelectionWeekInterface[] = [];

  constructor(
    protected readonly formBuilder: FormBuilder,
    private readonly translateService: TranslateService,
    private readonly toggleService: ToggleService
  ) {
    super();
  }

  public ngOnDestroy(): void {
    this.recurrentSwitchSubscription.unsubscribe();
    this.startDateTimeSubscription.unsubscribe();
    this.formGroupSubscription.unsubscribe();
  }

  public ngOnInit(): void {
    this.buildFormGroup();
    this.buildWeek();
  }

  public get dateLabel(): string {
    return this.isLive
      ? this.translateService.instant(
          'activity_planning.date_selection.start_date'
        )
      : this.translateService.instant(
          'activity_planning.date_selection.local_start_date'
        );
  }

  public get defaultDateTime(): DateTimeInterface {
    return {
      date: this.defaultValue?.dateSelection?.startDate,
      time: this.defaultValue?.dateSelection?.startTime,

      timezone: this.isLive
        ? moment?.tz.guess()
        : TzLookup(this.location?.latitude, this.location?.longitude),
    };
  }

  public get isTimezoneSelectionEnabled(): boolean {
    return this.toggleService.isEnabled(this.toggleNameEnum.TimezoneSelection);
  }

  public get recurrentSwitchFormControl(): FormControl {
    return this.formGroup.get('recurrentSwitch') as FormControl;
  }

  public get recurrentSwitchValue(): boolean {
    return this.recurrentSwitchFormControl.value;
  }

  public get weekControls(): FormControl[] {
    return (this.formGroup.get('week') as FormArray).controls as FormControl[];
  }

  private buildDaysValue(): { [key: string]: boolean } {
    const result: { [key: string]: boolean } = {};

    this.getFormValue('week').map((selected, index) => {
      result[this.weekConfig[index].formControlName] = selected;
    });

    return result;
  }

  private buildFormGroup(): void {
    this.formGroup = this.formBuilder.group({
      recurrentSwitch: [false],
      startDateTime: [{}],
      endDate: [''],
      week: this.buildWeekForm(),
    });

    this.recurrentSwitchSubscription = this.formGroup
      .get('recurrentSwitch')
      .valueChanges.subscribe((newValue: boolean) => {
        this.setEndDateValidator(newValue);
      });

    this.startDateTimeSubscription = this.formGroup
      .get('startDateTime')
      .valueChanges.subscribe((startDateTime: DateTimeInterface) => {
        this.endDateMin = new Date(startDateTime.date);
        this.endDateMin.setDate(this.endDateMin.getDate() + 1);

        this.endDateMax = new Date(startDateTime.date);
        this.endDateMax.setMonth(
          this.endDateMax.getMonth() + Number(this.recurrentDurationMonths)
        );
        this.setEndDateValidator(this.recurrentSwitchValue);
      });

    this.formGroupSubscription = this.formGroup.valueChanges.subscribe(() => {
      this.setDateSelectionValue();
    });

    // Default value
    if (!this.defaultValue?.dateSelection) {
      return;
    }

    const isRecurrent = Object.values(
      this.defaultValue.dateSelection?.recurrent?.days || {}
    ).includes(true);
    const week = this.weekConfig.map((dayConfig) => {
      return (
        this.defaultValue.dateSelection?.recurrent?.days[
          dayConfig.formControlName
        ] || false
      );
    });

    this.formGroup.setValue({
      recurrentSwitch: isRecurrent,
      startDateTime: {
        date: this.defaultValue.dateSelection?.startDate || '',
        time: this.defaultValue.dateSelection?.startTime || '',
        timezone: this.defaultValue.dateSelection.timezone || '',
      },
      endDate: isRecurrent
        ? this.defaultValue.dateSelection?.recurrent?.endDate
        : '',
      week,
    });
    this.formGroup.updateValueAndValidity();
  }

  private buildWeek(): void {
    this.week = this.weekConfig.map((config) => ({
      label: this.translateService.instant(config.labelKey),
      formControlName: config.formControlName,
      fullCalendarDayIndex: config.fullCalendarDayIndex,
    }));
  }

  private buildWeekForm(daysOfWeek = {}): FormArray {
    const weekControls = this.weekConfig.map((dayConfig) => {
      const isSelectedDay = daysOfWeek[dayConfig.formControlName];

      return this.formBuilder.control(isSelectedDay);
    });

    return this.formBuilder.array(weekControls);
  }

  private getFormValue(key: string) {
    return this.formGroup.get(key).value;
  }

  private setDateSelectionValue(): void {
    const recurrentValue = this.recurrentSwitchValue
      ? {
          days: this.buildDaysValue(),
          endDate: this.getFormValue('endDate'),
        }
      : null;

    const {
      date: startDate,
      time: startTime,
      timezone,
    }: DateTimeInterface = this.getFormValue('startDateTime');

    this.value = {
      startDate,
      startTime,
      recurrent: recurrentValue,
      timezone:
        timezone ||
        (this.isLive
          ? moment?.tz.guess()
          : TzLookup(this.location?.latitude, this.location?.longitude)),
    };
  }

  private setEndDateValidator(isShow: boolean): void {
    const endDateForm = this.formGroup.get('endDate');
    if (isShow) {
      endDateForm.setValidators([
        Validators.required,
        dateRangeValidator(this.endDateMin, this.endDateMax),
      ]);
    } else {
      endDateForm.clearValidators();
    }
    endDateForm.updateValueAndValidity();
  }

  public formatDate(date: Date): string {
    if (!date || !(date instanceof Date)) {
      return '';
    }
    return date.toISOString().split('T')[0];
  }

  public remove(): void {
    this.deleteCard.emit();
  }
}
